import React, { useState, useEffect, useRef } from 'react';
import api from '../../../../../api';
import { formatPhoneNumber, validatePhoneNumber, getPhoneErrorMessage } from '../../utils/phoneValidation';
import './ProfileWizard.css';

const ProfileWizard = ({ userId, existingProfile = null, onCancel, onComplete }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const errorRef = useRef(null);
  const wizardContentRef = useRef(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [usStates, setUsStates] = useState([]);
  const [errors, setErrors] = useState({});
  const [completedSteps, setCompletedSteps] = useState(new Set([1]));

  const [formData, setFormData] = useState({
    // Step 1: General Information
    id: existingProfile?.id || '',
    legalBusinessName: existingProfile?.legal_business_name || '',
    profileFriendlyName: existingProfile?.profile_friendly_name || '',
    country: existingProfile?.country || 'US',
    addressStreet1: existingProfile?.address_street1 || '',
    addressStreet2: existingProfile?.address_street2 || '',
    addressCity: existingProfile?.address_city || '',
    addressState: existingProfile?.address_state || '',
    addressPostalCode: existingProfile?.address_postal_code || '',

    // Step 2: Business Information
    businessIdentity: existingProfile?.business_identity || 'direct_customer',
    businessType: existingProfile?.business_type || '',
    businessIndustry: existingProfile?.business_industry || '',
    businessRegistrationIdType: existingProfile?.business_registration_id_type || 'EIN',
    businessRegistrationNumber: existingProfile?.business_registration_number || '',
    businessRegions: existingProfile?.business_regions || 'USA_AND_CANADA',
    businessWebsite: existingProfile?.business_website || '',

    // Step 3: Authorized Representatives
    representatives: existingProfile?.representatives?.map(rep => ({
      id: rep.id,
      firstName: rep.first_name || '',
      lastName: rep.last_name || '',
      email: rep.email || '',
      phoneNumber: formatPhoneNumber(rep.phone_number || ''),
      businessTitle: rep.business_title || '',
      jobPosition: rep.job_position || ''
    })) || [{
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      businessTitle: '',
      jobPosition: ''
    }],

    // Step 4: Notification Settings
    notificationEmail: existingProfile?.notification_email || '',
    statusCallbackWebhook: existingProfile?.status_callback_webhook || '',

    // Step 5: Terms and Conditions
    termsAccepted: existingProfile?.terms_accepted || false,
    user_id: userId
  });

  useEffect(() => {
    fetchUsStates();
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Scroll to top when error appears
  useEffect(() => {
    if (error && wizardContentRef.current) {
      wizardContentRef.current.scrollTop = 0;
    }
  }, [error]);

  const fetchUsStates = async () => {
    try {
      const response = await api.trustHubCentralized.getUsStates();
      if (response && response.data) {
        setUsStates(response.data);
      }
    } catch (err) {
      console.error('Error fetching US states:', err);
    }
  };

  const totalSteps = 5;

  const businessTypes = [
    { key: 'sole_proprietorship', text: 'Sole Proprietorship', value: 'Sole Proprietorship' },
    { key: 'partnership', text: 'Partnership', value: 'Partnership' },
    { key: 'llc', text: 'Limited Liability Corporation', value: 'Limited Liability Corporation' },
    { key: 'cooperative', text: 'Co-operative', value: 'Co-operative' },
    { key: 'nonprofit', text: 'Non-profit Corporation', value: 'Non-profit Corporation' },
    { key: 'corporation', text: 'Corporation', value: 'Corporation' }
  ];

  const businessIndustries = [
    { key: 'automotive', text: 'Automotive', value: 'AUTOMOTIVE' },
    { key: 'agriculture', text: 'Agriculture', value: 'AGRICULTURE' },
    { key: 'banking', text: 'Banking', value: 'BANKING' },
    { key: 'construction', text: 'Construction', value: 'CONSTRUCTION' },
    { key: 'consumer', text: 'Consumer', value: 'CONSUMER' },
    { key: 'education', text: 'Education', value: 'EDUCATION' },
    { key: 'engineering', text: 'Engineering', value: 'ENGINEERING' },
    { key: 'energy', text: 'Energy', value: 'ENERGY' },
    { key: 'oil_gas', text: 'Oil and Gas', value: 'OIL_AND_GAS' },
    { key: 'fmcg', text: 'Fast Moving Consumer Goods', value: 'FAST_MOVING_CONSUMER_GOODS' },
    { key: 'financial', text: 'Financial', value: 'FINANCIAL' },
    { key: 'fintech', text: 'Fintech', value: 'FINTECH' },
    { key: 'food_beverage', text: 'Food and Beverage', value: 'FOOD_AND_BEVERAGE' },
    { key: 'government', text: 'Government', value: 'GOVERNMENT' },
    { key: 'healthcare', text: 'Healthcare', value: 'HEALTHCARE' },
    { key: 'hospitality', text: 'Hospitality', value: 'HOSPITALITY' },
    { key: 'insurance', text: 'Insurance', value: 'INSURANCE' },
    { key: 'legal', text: 'Legal', value: 'LEGAL' },
    { key: 'manufacturing', text: 'Manufacturing', value: 'MANUFACTURING' },
    { key: 'media', text: 'Media', value: 'MEDIA' },
    { key: 'online', text: 'Online', value: 'ONLINE' },
    { key: 'professional_services', text: 'Professional Services', value: 'PROFESSIONAL_SERVICES' },
    { key: 'raw_materials', text: 'Raw Materials', value: 'RAW_MATERIALS' },
    { key: 'real_estate', text: 'Real Estate', value: 'REAL_ESTATE' },
    { key: 'religion', text: 'Religion', value: 'RELIGION' },
    { key: 'retail', text: 'Retail', value: 'RETAIL' },
    { key: 'jewelry', text: 'Jewelry', value: 'JEWELRY' },
    { key: 'technology', text: 'Technology', value: 'TECHNOLOGY' },
    { key: 'telecommunications', text: 'Telecommunications', value: 'TELECOMMUNICATIONS' },
    { key: 'transportation', text: 'Transportation', value: 'TRANSPORTATION' },
    { key: 'travel', text: 'Travel', value: 'TRAVEL' },
    { key: 'electronics', text: 'Electronics', value: 'ELECTRONICS' },
    { key: 'not_for_profit', text: 'Not For Profit', value: 'NOT_FOR_PROFIT' }
  ];

  const businessRegistrationTypes = [
    { key: 'ein', text: 'Employer Identification Number (EIN)', value: 'EIN' }
  ];

  const jobPositions = [
    { key: 'director', text: 'Director', value: 'Director' },
    { key: 'gm', text: 'GM', value: 'GM' },
    { key: 'vp', text: 'VP', value: 'VP' },
    { key: 'ceo', text: 'CEO', value: 'CEO' },
    { key: 'cfo', text: 'CFO', value: 'CFO' },
    { key: 'general_counsel', text: 'General Counsel', value: 'General Counsel' },
    { key: 'other', text: 'Other', value: 'Other' },
  ];

  // Validation functions
  const validateEIN = (ein) => {
    // EIN format: XX-XXXXXXX (2 digits, hyphen, 7 digits)
    const einRegex = /^\d{2}-\d{7}$/;
    return einRegex.test(ein);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateWebsite = (website) => {
    if (!website) return false; // Website is required
    const websiteRegex = /^https?:\/\/.+\..+/;
    return websiteRegex.test(website);
  };

  // Formatting functions
  const formatEIN = (value) => {
    // Remove all non-digits
    const digits = value.replace(/\D/g, '');
    // Format as XX-XXXXXXX
    if (digits.length <= 2) {
      return digits;
    } else if (digits.length <= 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    } else {
      return `${digits.slice(0, 2)}-${digits.slice(2, 9)}`;
    }
  };

  const nameRegex = /^(?!\d+$)(?!.*[-'\s]{2})[\p{L}0-9][\p{L}0-9\s'\-\.]{1,48}[\p{L}0-9\.]$/u;

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    // Apply formatting for specific fields
    if (field === 'businessRegistrationNumber') {
      formattedValue = formatEIN(value);
    }

    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleRepresentativeChange = (index, field, value) => {
    let formattedValue = value;

    // Apply formatting for phone number field
    if (field === 'phoneNumber') {
      formattedValue = formatPhoneNumber(value);
    }

    const newRepresentatives = [...formData.representatives];
    newRepresentatives[index][field] = formattedValue;
    setFormData(prev => ({
      ...prev,
      representatives: newRepresentatives
    }));

    // Clear error when user starts typing
    const errorKey = `rep_${index}_${field}`;
    if (errors[errorKey]) {
      setErrors(prev => ({
        ...prev,
        [errorKey]: null
      }));
    }
  };

  const addSecondRepresentative = () => {
    setFormData(prev => ({
      ...prev,
      representatives: [...prev.representatives, {
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        businessTitle: '',
        jobPosition: ''
      }]
    }));
  };

  const removeSecondRepresentative = () => {
    setFormData(prev => ({
      ...prev,
      representatives: [prev.representatives[0]]
    }));
  };

  // Validation helper functions
  const checkLength = (value, min, max, fieldName) => {
    if (value.trim().length < min) return `${fieldName} must be at least ${min} characters.`;
    if (value.trim().length > max) return `${fieldName} cannot exceed ${max} characters.`;
    return null;
  };

  const validateName = (value, fieldName, min = 2, max = 50) => {
    if (!value || value.trim() === '') return `${fieldName} is required`;
    const err = checkLength(value, min, max, fieldName);
    if (err) return err;
    if (!nameRegex.test(value.trim())) return `${fieldName} contains invalid characters`;
    return null;
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      // Step 1: General Information
      case 1:
        // Legal business name
        if (!formData.legalBusinessName) {
          newErrors.legalBusinessName = 'Legal business name is required';
        } else {
          const err = validateName(formData.legalBusinessName, 'Legal business name', 3, 50);
          if (err) newErrors.legalBusinessName = err;
        }

        // Profile friendly name
        if (!formData.profileFriendlyName) {
          newErrors.profileFriendlyName = 'Profile friendly name is required';
        } else {
          const err = validateName(formData.profileFriendlyName, 'Profile friendly name', 3, 50);
          if (err) newErrors.profileFriendlyName = err;
        }

        // Country
        if (!formData.country) newErrors.country = 'Country is required';

        // Street
        if (!formData.addressStreet1) {
          newErrors.addressStreet1 = 'Street address is required';
        } else {
          const err = checkLength(formData.addressStreet1, 3, 100, 'Street address');
          if (err) newErrors.addressStreet1 = err;
        }

        // City
        if (!formData.addressCity) {
          newErrors.addressCity = 'City is required';
        } else {
          const err = validateName(formData.addressCity, 'City', 2, 50);
          if (err) newErrors.addressCity = err;
        }

        // State
        if (!formData.addressState) {
          newErrors.addressState = 'State/Province is required';
        }

        // Postal Code
        if (!formData.addressPostalCode) {
          newErrors.addressPostalCode = 'Postal code is required';
        } else if (!/^\d{5,6}$/.test(formData.addressPostalCode)) {
          newErrors.addressPostalCode = 'Postal code must be 5–6 digits';
        }
        break;

      // Step 2: Business Information
      case 2:
        if (!formData.businessIdentity) newErrors.businessIdentity = 'Business identity is required';
        if (!formData.businessType) newErrors.businessType = 'Business type is required';
        if (!formData.businessIndustry) newErrors.businessIndustry = 'Business industry is required';
        if (!formData.businessRegistrationIdType) newErrors.businessRegistrationIdType = 'Business registration ID type is required';

        if (!formData.businessRegistrationNumber) {
          newErrors.businessRegistrationNumber = 'Business registration number is required';
        } else if (!validateEIN(formData.businessRegistrationNumber)) {
          newErrors.businessRegistrationNumber = 'EIN must be in format XX-XXXXXXX';
        }

        if (!formData.businessWebsite) {
          newErrors.businessWebsite = 'Business website is required';
        } else if (!validateWebsite(formData.businessWebsite)) {
          newErrors.businessWebsite = 'Website must be a valid URL (e.g., https://example.com)';
        }
        break;

      // Step 3: Authorized Representatives
      case 3:
        if (formData.representatives.length < 1) {
          newErrors.representatives = 'At least 1 authorized representative is required';
        }

        formData.representatives.forEach((rep, index) => {
          // First Name
          const firstNameErr = validateName(rep.firstName, 'First name');
          if (firstNameErr) newErrors[`rep_${index}_firstName`] = firstNameErr;

          // Last Name
          const lastNameErr = validateName(rep.lastName, 'Last name');
          if (lastNameErr) newErrors[`rep_${index}_lastName`] = lastNameErr;

          // Email
          if (!rep.email) newErrors[`rep_${index}_email`] = 'Email is required';
          else if (!validateEmail(rep.email))
            newErrors[`rep_${index}_email`] = 'Please enter a valid email address';

          // Phone
          if (!rep.phoneNumber) newErrors[`rep_${index}_phoneNumber`] = 'Phone number is required';
          else if (!validatePhoneNumber(rep.phoneNumber))
            newErrors[`rep_${index}_phoneNumber`] = 'Phone number must contain exactly 10 digits.';

          // Business Title
          const businessTitleErr = validateName(rep.businessTitle, 'Business title');
          if (businessTitleErr) newErrors[`rep_${index}_businessTitle`] = businessTitleErr;

          // Job Position (not required in old form validation but field exists)
          if (rep.jobPosition && !jobPositions.find(j => j.value === rep.jobPosition)) {
            newErrors[`rep_${index}_jobPosition`] = 'Please select a valid job position';
          }
        });
        break;

      // Step 4: Notification Settings
      case 4:
        if (!formData.notificationEmail) newErrors.notificationEmail = 'Notification email is required';
        else if (!validateEmail(formData.notificationEmail))
          newErrors.notificationEmail = 'Please enter a valid email address';
        break;

      // Step 5: Terms
      case 5:
        if (!formData.termsAccepted) newErrors.termsAccepted = 'You must accept the terms of service';
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const canAccessStep = (stepNumber) => {
    return completedSteps.has(stepNumber) || stepNumber === currentStep;
  };

  const markStepCompleted = (stepNumber) => {
    setCompletedSteps(prev => new Set([...prev, stepNumber]));
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setError(null);
      markStepCompleted(currentStep);
      const nextStep = Math.min(currentStep + 1, totalSteps);
      // Mark next step as accessible
      markStepCompleted(nextStep);
      setCurrentStep(nextStep);
    } else {
      // Scroll to top to show validation errors
      setTimeout(() => {
        if (wizardContentRef.current) {
          wizardContentRef.current.scrollTop = 0;
        }
      }, 100);
    }
  };

  const handlePrevious = () => {
    setError(null);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      // Scroll to top to show validation errors
      setTimeout(() => {
        if (wizardContentRef.current) {
          wizardContentRef.current.scrollTop = 0;
        }
      }, 100);
      return;
    }

    // Prevent multiple clicks - if already loading, do nothing
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        user_id: userId,
      };

      let response;
      if (existingProfile && existingProfile.id) {
        // Update existing profile
        response = await api.trustHubCentralized.updateProfile(payload);
      } else {
        // Create new profile
        response = await api.trustHubCentralized.createProfile(payload);
      }

      // Check if response is successful
      if (response.success === true || response.status === true) {
        // API returned success - show message and close popup
        setError(null);
        const isCreating = !existingProfile || !existingProfile.id;
        setSuccessMessage(response.message || (isCreating ? 'Business profile created successfully' : 'Profile updated successfully'));
        setLoading(false);

        // Close popup after 3 seconds and refresh dashboard
        setTimeout(() => {
          onComplete(response);
        }, 3000);
      } else {
        // API returned error - show error message and keep form open
        setError(response.message || 'Failed to save profile');
        setLoading(false);
        // Scroll to top to show error message
        setTimeout(() => {
          if (wizardContentRef.current) {
            wizardContentRef.current.scrollTop = 0;
          }
        }, 100);
      }
    } catch (err) {
      console.error('Error saving profile:', err);
      // Check for network errors (AC15)
      if (!err.response) {
        setError('Unable to connect. Please check your internet connection.');
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        setError("You don't have permission to perform this action");
      } else {
        setError(err.response?.data?.message || 'Unable to save profile. Please try again.');
      }
      setLoading(false);
      // Scroll to top to show error message
      setTimeout(() => {
        if (wizardContentRef.current) {
          wizardContentRef.current.scrollTop = 0;
        }
      }, 100);
    }
  };

  const steps = [
    { number: 1, icon: '🏢', title: 'General Information', description: 'Business details' },
    { number: 2, icon: '💼', title: 'Business Information', description: 'Registration details' },
    { number: 3, icon: '👤', title: 'Authorized Representatives', description: 'Contact persons' },
    { number: 4, icon: '📧', title: 'Notification Settings', description: 'Email preferences' },
    { number: 5, icon: '✅', title: 'Terms of Service', description: 'Accept terms' },
  ];

  const renderStepIndicator = () => (
    <div className="step-sidebar">
      <div className="step-sidebar-header">
        <h3>{existingProfile ? 'Edit Profile' : 'Create Profile'}</h3>
        <p>Step {currentStep} of {totalSteps}</p>
      </div>
      <div className="step-list">
        {steps.map(step => (
          <div
            key={step.number}
            className={`step-item ${step.number === currentStep ? 'active' : ''} ${completedSteps.has(step.number) ? 'completed' : ''} ${canAccessStep(step.number) ? 'clickable' : 'disabled'}`}
            onClick={() => canAccessStep(step.number) && setCurrentStep(step.number)}
          >
            <div className="step-item-indicator">
              {completedSteps.has(step.number) ? (
                <span className="step-checkmark">✓</span>
              ) : (
                <span className="step-icon">{step.icon}</span>
              )}
            </div>
            <div className="step-item-content">
              <div className="step-item-title">{step.title}</div>
              <div className="step-item-description">{step.description}</div>
            </div>
            {/* {completedSteps.has(step.number) && <div className="step-completed-badge">✓</div>} */}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="wizard-step">
      <h3>General Information</h3>
      <p>Provide your organization's basic information:</p>

      <div className="form-grid">
        <div className="form-group">
          <label>Legal Business Name <span className="required">*</span></label>
          <input
            type="text"
            value={formData.legalBusinessName}
            onChange={(e) => handleInputChange('legalBusinessName', e.target.value)}
            placeholder="Exact legal business name as registered"
            className={errors.legalBusinessName ? 'error' : ''}
          />
          {errors.legalBusinessName && <span className="error-message">{errors.legalBusinessName}</span>}
        </div>

        <div className="form-group">
          <label>Profile Friendly Name <span className="required">*</span></label>
          <input
            type="text"
            value={formData.profileFriendlyName}
            onChange={(e) => handleInputChange('profileFriendlyName', e.target.value)}
            placeholder="Short label for your organization"
            className={errors.profileFriendlyName ? 'error' : ''}
          />
          {errors.profileFriendlyName && <span className="error-message">{errors.profileFriendlyName}</span>}
        </div>

        <div className="form-group full-width">
          <label>Country <span className="required">*</span></label>
          <select
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            disabled
            className={errors.country ? 'error' : ''}
          >
            <option value="US">United States</option>
          </select>
          {errors.country && <span className="error-message">{errors.country}</span>}
        </div>

        <div className="form-group">
          <label>Address Street 1 <span className="required">*</span></label>
          <input
            type="text"
            value={formData.addressStreet1}
            onChange={(e) => handleInputChange('addressStreet1', e.target.value)}
            placeholder="Physical street address"
            className={errors.addressStreet1 ? 'error' : ''}
          />
          {errors.addressStreet1 && <span className="error-message">{errors.addressStreet1}</span>}
        </div>

        <div className="form-group">
          <label>Address Street 2</label>
          <input
            type="text"
            value={formData.addressStreet2}
            onChange={(e) => handleInputChange('addressStreet2', e.target.value)}
            placeholder="Additional address line (optional)"
          />
        </div>

        <div className="form-grid-three-col">
          <div className="form-group">
            <label>City <span className="required">*</span></label>
            <input
              type="text"
              value={formData.addressCity}
              onChange={(e) => handleInputChange('addressCity', e.target.value)}
              placeholder="City"
              className={errors.addressCity ? 'error' : ''}
            />
            {errors.addressCity && <span className="error-message">{errors.addressCity}</span>}
          </div>

          <div className="form-group">
            <label>State or Province <span className="required">*</span></label>
            <select
              value={formData.addressState}
              onChange={(e) => handleInputChange('addressState', e.target.value)}
              className={errors.addressState ? 'error' : ''}
            >
              <option value="">Select State</option>
              {usStates.map(state => (
                <option key={state.id} value={state.id}>{state.name}</option>
              ))}
            </select>
            {errors.addressState && <span className="error-message">{errors.addressState}</span>}
          </div>

          <div className="form-group">
            <label>Postal Code <span className="required">*</span></label>
            <input
              type="text"
              value={formData.addressPostalCode}
              onChange={(e) => handleInputChange('addressPostalCode', e.target.value)}
              placeholder="Postal code"
              className={errors.addressPostalCode ? 'error' : ''}
            />
            {errors.addressPostalCode && <span className="error-message">{errors.addressPostalCode}</span>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="wizard-step">
      <h3>Business Information</h3>
      <p>Provide details about your business:</p>

      <div className="form-grid">
        <div className="form-group">
          <label>Business Type <span className="required">*</span></label>
          <select
            value={formData.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
            className={errors.businessType ? 'error' : ''}
          >
            <option value="">Select business type</option>
            {businessTypes.map(type => (
              <option key={type.key} value={type.value}>{type.text}</option>
            ))}
          </select>
          {errors.businessType && <span className="error-message">{errors.businessType}</span>}
        </div>

        <div className="form-group">
          <label>Business Industry <span className="required">*</span></label>
          <select
            value={formData.businessIndustry}
            onChange={(e) => handleInputChange('businessIndustry', e.target.value)}
            className={errors.businessIndustry ? 'error' : ''}
          >
            <option value="">Select industry</option>
            {businessIndustries.map(industry => (
              <option key={industry.key} value={industry.value}>{industry.text}</option>
            ))}
          </select>
          {errors.businessIndustry && <span className="error-message">{errors.businessIndustry}</span>}
          <small className="field-hint">If you're unsure, you're likely a "consumer" business.</small>
        </div>

        <div className="form-group">
          <label>Business Registration ID Type <span className="required">*</span></label>
          <select
            value={formData.businessRegistrationIdType}
            disabled
            className={errors.businessRegistrationIdType ? 'error' : ''}
          >
            <option value="EIN">Employer Identification Number (EIN)</option>
          </select>
          {errors.businessRegistrationIdType && <span className="error-message">{errors.businessRegistrationIdType}</span>}
        </div>

        <div className="form-group">
          <label>Business Registration Number <span className="required">*</span></label>
          <input
            type="text"
            value={formData.businessRegistrationNumber}
            onChange={(e) => handleInputChange('businessRegistrationNumber', e.target.value)}
            placeholder="12-3456789"
            className={errors.businessRegistrationNumber ? 'error' : ''}
          />
          {errors.businessRegistrationNumber && <span className="error-message">{errors.businessRegistrationNumber}</span>}
        </div>

        <div className="form-group">
          <label>Business Regions of Operations <span className="required">*</span></label>
          <input
            type="text"
            value={formData.businessRegions === 'USA_AND_CANADA' ? 'USA and Canada' : formData.businessRegions}
            disabled
          />
        </div>

        <div className="form-group">
          <label>Business Website <span className="required">*</span></label>
          <input
            type="url"
            value={formData.businessWebsite}
            onChange={(e) => handleInputChange('businessWebsite', e.target.value)}
            placeholder="https://your-website.com"
            className={errors.businessWebsite ? 'error' : ''}
          />
          {errors.businessWebsite && <span className="error-message">{errors.businessWebsite}</span>}
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="wizard-step">
      <h3>Authorized Representatives</h3>
      <p>
        Provide information about authorized representatives. The first representative is required; the second is optional.
      </p>

      {formData.representatives.map((rep, index) => (
        <div key={index} style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>
              Authorized Representative #{index + 1} {index === 1 ? '(Optional)' : ''}
            </h4>
            {index === 1 && (
              <button
                type="button"
                onClick={removeSecondRepresentative}
                className="btn linear-gradient zy-save-btn"
                style={{ fontSize: '12px', padding: '5px 10px' }}
              >
                Remove
              </button>
            )}
          </div>

          <div className="form-grid">
            <div className="form-group">
              <label>First Name <span className="required">*</span></label>
              <input
                type="text"
                placeholder="First name"
                value={rep.firstName}
                onChange={(e) => handleRepresentativeChange(index, 'firstName', e.target.value)}
                className={errors[`rep_${index}_firstName`] ? 'error' : ''}
              />
              {errors[`rep_${index}_firstName`] && <span className="error-message">{errors[`rep_${index}_firstName`]}</span>}
            </div>

            <div className="form-group">
              <label>Last Name <span className="required">*</span></label>
              <input
                type="text"
                placeholder="Last name"
                value={rep.lastName}
                onChange={(e) => handleRepresentativeChange(index, 'lastName', e.target.value)}
                className={errors[`rep_${index}_lastName`] ? 'error' : ''}
              />
              {errors[`rep_${index}_lastName`] && <span className="error-message">{errors[`rep_${index}_lastName`]}</span>}
            </div>

            <div className="form-group">
              <label>Email <span className="required">*</span></label>
              <input
                type="email"
                placeholder="name@example.com"
                value={rep.email}
                onChange={(e) => handleRepresentativeChange(index, 'email', e.target.value)}
                className={errors[`rep_${index}_email`] ? 'error' : ''}
              />
              {errors[`rep_${index}_email`] && <span className="error-message">{errors[`rep_${index}_email`]}</span>}
            </div>

            <div className="form-group">
              <label>Phone Number <span className="required">*</span></label>
              <input
                type="tel"
                placeholder="10-digit phone number"
                value={rep.phoneNumber}
                onChange={(e) => handleRepresentativeChange(index, 'phoneNumber', e.target.value)}
                className={errors[`rep_${index}_phoneNumber`] ? 'error' : ''}
              />
              {errors[`rep_${index}_phoneNumber`] && <span className="error-message">{errors[`rep_${index}_phoneNumber`]}</span>}
            </div>

            <div className="form-group">
              <label>Business Title <span className="required">*</span></label>
              <input
                type="text"
                placeholder="Business title or position (CEO, Manager)"
                value={rep.businessTitle}
                onChange={(e) => handleRepresentativeChange(index, 'businessTitle', e.target.value)}
                className={errors[`rep_${index}_businessTitle`] ? 'error' : ''}
              />
              {errors[`rep_${index}_businessTitle`] && <span className="error-message">{errors[`rep_${index}_businessTitle`]}</span>}
            </div>

            <div className="form-group">
              <label>Job Position <span className="required">*</span></label>
              <select
                value={rep.jobPosition}
                onChange={(e) => handleRepresentativeChange(index, 'jobPosition', e.target.value)}
                className={errors[`rep_${index}_jobPosition`] ? 'error' : ''}
              >
                <option value="">Select job position</option>
                {jobPositions.map(position => (
                  <option key={position.key} value={position.value}>{position.text}</option>
                ))}
              </select>
              {errors[`rep_${index}_jobPosition`] && <span className="error-message">{errors[`rep_${index}_jobPosition`]}</span>}
            </div>
          </div>
        </div>
      ))}

      {formData.representatives.length === 1 && (
        <div style={{ textAlign: 'center' }}>
          <button
            type="button"
            onClick={addSecondRepresentative}
            className="btn linear-gradient zy-save-btn ml-2"
            style={{ fontSize: '14px', padding: '8px 16px' }}
          >
            + Add Second Representative (Optional)
          </button>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="wizard-step">
      <h3>Notification Settings</h3>
      <p>Configure how you want to receive notifications about your Business profile:</p>

      <div className="form-grid">
        <div className="form-group full-width">
          <label>Email Address <span className="required">*</span></label>
          <input
            type="email"
            placeholder="notifications@yourcompany.com"
            value={formData.notificationEmail}
            onChange={(e) => handleInputChange('notificationEmail', e.target.value)}
            className={errors.notificationEmail ? 'error' : ''}
          />
          {errors.notificationEmail && <span className="error-message">{errors.notificationEmail}</span>}
        </div>
      </div>


    </div>
  );

  const renderStep5 = () => (
    <div className="wizard-step">
      <h3>Terms of Service and Privacy Policy</h3>
      <p>
        Please review and accept the following terms before submitting your Business profile.
      </p>

      <div className="terms-section">
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={formData.termsAccepted}
            onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
            style={{ accentColor: 'rgb(15, 98, 254)' }}
          />
          <span>
            I agree to Twilio's{' '}
            <a
              href="https://www.twilio.com/en-us/legal/tos"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0f62fe', textDecoration: 'underline' }}
            >
              Terms of Service
            </a>{' '}
            and{' '}
            <a
              href="https://www.twilio.com/en-us/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#0f62fe', textDecoration: 'underline' }}
            >
              Privacy Policy
            </a>
            <span style={{ color: 'red' }}> *</span>
          </span>
        </label>

        {errors.termsAccepted && (
          <span className="error-message">{errors.termsAccepted}</span>
        )}
      </div>
    </div>
  );

  return (
    <div className="profile-wizard">
      {renderStepIndicator()}

      <div className="wizard-main">
        <div className="wizard-content" ref={wizardContentRef}>
          {successMessage && (
            <div className="alert alert-success" style={{ marginBottom: '20px' }}>
              {successMessage}
            </div>
          )}

          {error && (
            <div className="alert alert-danger" ref={errorRef}>
              {error}
            </div>
          )}

          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}
          {currentStep === 4 && renderStep4()}
          {currentStep === 5 && renderStep5()}
        </div>

        <div className="wizard-footer">
         
          <div className="wizard-nav-buttons">
             <button className="btn linear-gradient-cancel yr-cancelbtn mg-r" onClick={onCancel} disabled={loading}>
            Cancel
          </button>
            {currentStep > 1 && (
              <button className="btn linear-gradient-cancel yr-cancelbtn mg-r" onClick={handlePrevious} disabled={loading}>
                Go Back
              </button>
            )}
            {currentStep < totalSteps ? (
              <button className="btn linear-gradient zy-save-btn ml-2" onClick={handleNext} disabled={loading}>
                Next
              </button>
            ) : (
              <button className="btn linear-gradient zy-save-btn ml-2" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Submitting...' : 'Submit'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileWizard;
